import React, { useEffect, useState } from "react";
import {
  ArrowUpRight, Check, MousePointerClick, ShieldCheck, Sparkles,
  TrendingDown, TrendingUp, Zap,
} from "lucide-react";

import { KenBurnsShot, SceneCaption } from "../KenBurnsShot";
import {
  CASH_IMPACT_LABEL, CASH_IMPACT_USD, NET_MARGIN_PCT, OPPS_COUNT,
  REVENUE_VELOCITY_USD, ROI_MULTIPLIER, fmtUsd, fmtUsdK,
} from "../../landing/aigoMetrics";

const PLATFORM_URL = "https://platform.aigrowthoperator.xyz";

/**
 * 45s growth-arc walkthrough — every number is real, every visual
 * beat is anchored to the actual product screenshots. The emotional
 * peak is S4: revenue climbs + margin expands + ROI locks in.
 *
 * Sequence (positionally coupled to SCENE_DURATIONS_MS below):
 *   S1 · 0–6s   · Problem in one number (Growth Score dropping)
 *   S2 · 6–15s  · Command Center diagnoses +$76.8k across 73 opps
 *   S3 · 15–24s · 1-click Implement · ticker jumps
 *   S4 · 24–40s · THE PAYOFF · split-screen BEFORE vs AFTER
 *                  Revenue climbs to $88.6k · Margin 48%→53.7% · ROI 7.32x
 *   S5 · 40–45s · End card CTA
 *
 * Total = 6 + 9 + 9 + 16 + 5 = 45s.
 */

export const SCENE_DURATIONS_MS = [6000, 9000, 9000, 16000, 5000];
export const TOTAL_DURATION_MS  = SCENE_DURATIONS_MS.reduce((a, b) => a + b, 0);

// ==================== S1 · Problem ====================
const SceneProblem = () => {
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setPulse(true), 500);
    return () => clearTimeout(t);
  }, []);
  return (
    <>
      <KenBurnsShot
        src="/screenshots/hero.png"
        alt="Growth Score 20/100 · margin bleeding"
        from="center" to="center" zoom={1.18}
        hotspot={
          <>
            {/* Dim overlay to draw eye to the red 20/100 */}
            <div className="absolute inset-0 bg-slate-900/25 z-10" />
            {/* Red pulsing ring roughly over the Growth Score dial */}
            <div
              className="absolute z-20 rounded-full border-2 border-rose-400"
              style={{
                left: "25%", top: "36%", width: "9%", aspectRatio: "1",
                animation: pulse ? "pd-red-pulse 1.4s ease-in-out infinite" : "none",
              }}
            />
            {/* Big red delta badge floating in top-left */}
            <div
              className="absolute z-30 top-6 left-6 sm:top-10 sm:left-10 inline-flex items-center gap-2 px-3 py-2 rounded-2xl bg-rose-500 text-white text-xs sm:text-sm font-extrabold shadow-2xl shadow-rose-500/40"
              style={{ animation: "pd-pop 500ms cubic-bezier(.2,.8,.2,1) 400ms both" }}
            >
              <TrendingDown className="w-4 h-4" />
              Growth Score 20/100
            </div>
          </>
        }
      />
      <SceneCaption
        kicker={<>Where most Shopify stores are</>}
        headline="Margin bleeding."
        accent="Revenue slipping."
        sub="Most operators can't see it until it hits the bank."
      />
    </>
  );
};

// ==================== S2 · Diagnose ====================
const SceneDiagnose = () => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const t0 = performance.now();
    const step = (now) => {
      const p = Math.min(1, (now - t0) / 2400);
      const eased = 1 - Math.pow(1 - p, 3);
      setCount(Math.round(CASH_IMPACT_USD * eased));
      if (p < 1) requestAnimationFrame(step);
    };
    const timer = setTimeout(() => requestAnimationFrame(step), 1200);
    return () => clearTimeout(timer);
  }, []);
  return (
    <>
      <KenBurnsShot
        src="/screenshots/hero.png"
        alt="AI CFO diagnosed +$76.8k across 73 opps"
        from="left" to="right" zoom={1.06}
        hotspot={
          <div
            className="absolute z-30 left-1/2 top-[55%]"
            style={{
              transform: "translate(-50%,-50%)",
              animation: "pd-pop 600ms cubic-bezier(.2,.8,.2,1) 500ms both",
            }}
          >
            <div className="px-4 py-2.5 rounded-2xl bg-slate-900 text-white text-sm sm:text-base font-extrabold shadow-2xl shadow-slate-900/50 inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              Approve-ready ·
              <span className="text-emerald-400 text-lg sm:text-xl tabular-nums">
                {count > 0 ? `+${fmtUsdK(count)}` : "$0"}
              </span>
              · {OPPS_COUNT} opps
            </div>
          </div>
        }
      />
      <SceneCaption
        kicker={<><Sparkles className="w-3 h-3" /> AI CFO</>}
        headline="Diagnoses"
        accent={`${CASH_IMPACT_LABEL} across ${OPPS_COUNT} opportunities.`}
        sub="Every leak in plain English. Every fix ranked by $ impact."
      />
    </>
  );
};

// ==================== S3 · Implement ====================
const SceneImplement = () => {
  const [applied, setApplied] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setApplied(true), 3600);
    return () => clearTimeout(t);
  }, []);
  return (
    <>
      <KenBurnsShot
        src="/screenshots/implement-feed.png"
        alt="1-click Implement panel"
        from="left" to="right" zoom={1.06}
        hotspot={
          <>
            {!applied && (
              <div
                className="absolute z-30 pointer-events-none"
                style={{
                  right: "8%", top: "28%",
                  animation: "pd-ring 1.5s ease-out infinite",
                  borderRadius: "9999px",
                }}
              >
                <div className="w-16 h-16 rounded-full border-2 border-emerald-400/80" />
              </div>
            )}
            {!applied && (
              <div
                className="absolute z-30 pointer-events-none"
                style={{
                  right: "6%", top: "32%",
                  animation: "pd-fadein 500ms ease-out 2400ms both",
                }}
              >
                <MousePointerClick className="w-7 h-7 text-white drop-shadow-lg" />
              </div>
            )}
            {applied && (
              <div
                className="absolute z-30"
                style={{
                  left: "50%", top: "48%",
                  transform: "translate(-50%,-50%)",
                  animation: "pd-pop 500ms cubic-bezier(.2,.8,.2,1) both",
                }}
              >
                <div className="px-4 py-2.5 rounded-2xl bg-emerald-500 text-white text-sm font-extrabold shadow-2xl shadow-emerald-500/50 inline-flex items-center gap-2">
                  <Check className="w-5 h-5" /> Applied · Revenue climbing
                </div>
              </div>
            )}
          </>
        }
      />
      <SceneCaption
        kicker={<><Zap className="w-3 h-3" /> 1-click</>}
        headline="Approve the fix."
        accent="Revenue starts climbing."
        sub="Preview first. Undo anytime. Every fix ships safe."
      />
    </>
  );
};

// ==================== S4 · THE PAYOFF (split-screen) ====================
const SceneGrowthPayoff = () => {
  const [phase, setPhase] = useState(0);
  const [rev, setRev] = useState(48700);
  const [margin, setMargin] = useState(48.0);
  const [roiShown, setRoiShown] = useState(false);

  useEffect(() => {
    // Beat 1 (0–3s): scene intro; hold BEFORE/AFTER visible
    const t1 = setTimeout(() => setPhase(1), 2000);   // start Revenue count-up
    const t2 = setTimeout(() => setPhase(2), 6000);   // start Margin gauge expansion
    const t3 = setTimeout(() => setPhase(3), 10000);  // reveal ROI
    const t4 = setTimeout(() => setPhase(4), 13500);  // pulse all metrics

    return () => [t1, t2, t3, t4].forEach(clearTimeout);
  }, []);

  useEffect(() => {
    if (phase !== 1) return;  // start ONCE — no re-entry when phase advances
    const t0 = performance.now();
    let raf;
    const step = (now) => {
      const p = Math.min(1, (now - t0) / 2200);
      const eased = 1 - Math.pow(1 - p, 3);
      setRev(Math.round(48700 + (REVENUE_VELOCITY_USD - 48700) * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => raf && cancelAnimationFrame(raf);
  }, [phase]);

  useEffect(() => {
    if (phase !== 2) return;  // start ONCE — margin gauge expansion
    const t0 = performance.now();
    let raf;
    const step = (now) => {
      const p = Math.min(1, (now - t0) / 2600);
      const eased = 1 - Math.pow(1 - p, 3);
      setMargin(+(48.0 + (NET_MARGIN_PCT - 48.0) * eased).toFixed(1));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => raf && cancelAnimationFrame(raf);
  }, [phase]);

  useEffect(() => {
    if (phase >= 3) setRoiShown(true);
  }, [phase]);

  const glow = phase >= 4;

  return (
    <div className="absolute inset-0 flex flex-col"
      style={{
        background:
          "radial-gradient(60% 60% at 50% 40%, rgba(16,185,129,0.10), transparent 70%), linear-gradient(180deg,#f8fafc, #ffffff)",
        animation: "pd-fadein 500ms ease-out",
      }}
    >
      {/* Scene kicker */}
      <div className="pt-6 sm:pt-8 flex justify-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-[0.18em] shadow-lg shadow-emerald-500/30"
          style={{ animation: "cap-in 500ms ease-out both" }}
        >
          <Sparkles className="w-3 h-3" /> The payoff
        </div>
      </div>

      {/* Split-screen BEFORE vs AFTER */}
      <div className="flex-1 grid grid-cols-2 gap-2 sm:gap-3 px-3 sm:px-6 py-4 sm:py-5">
        {/* BEFORE */}
        <div
          className="rounded-2xl border border-slate-200 bg-slate-50 flex flex-col p-3 sm:p-5 relative overflow-hidden"
          style={{ opacity: phase >= 4 ? 0.55 : 1, transition: "opacity 800ms ease-out" }}
        >
          <div className="text-[9px] sm:text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400">Before</div>
          <div className="mt-2 flex items-baseline gap-1">
            <span className="text-slate-500 text-2xl sm:text-4xl font-extrabold tabular-nums">$48.7k</span>
          </div>
          <div className="text-[10px] sm:text-xs text-slate-500 mt-0.5">Revenue · today</div>

          <div className="mt-4 flex items-center gap-3">
            <RingGauge value={48} size={64} color="#94a3b8" />
            <div className="min-w-0">
              <div className="text-slate-600 text-sm sm:text-base font-bold tabular-nums">48.0%</div>
              <div className="text-[9px] tracking-[0.14em] uppercase font-bold text-slate-400">Net margin</div>
            </div>
          </div>

          <div className="mt-auto pt-3">
            <div className="text-[10px] tracking-[0.14em] uppercase font-bold text-slate-400">ROI</div>
            <div className="text-slate-500 text-lg font-bold">—</div>
          </div>
        </div>

        {/* AFTER */}
        <div
          className="rounded-2xl border-2 bg-white flex flex-col p-3 sm:p-5 relative overflow-hidden shadow-xl transition-all"
          style={{
            borderColor: glow ? "#10b981" : "#a7f3d0",
            boxShadow: glow ? "0 0 40px rgba(16,185,129,0.25)" : "0 8px 24px rgba(15,23,42,0.06)",
          }}
        >
          <div className="text-emerald-700 text-[9px] sm:text-[10px] tracking-[0.2em] uppercase font-bold flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> After · in one click
          </div>

          {/* Revenue */}
          <div className="mt-2 flex items-baseline gap-1">
            <span
              className="text-emerald-700 text-2xl sm:text-4xl font-extrabold tabular-nums"
              style={{ textShadow: glow ? "0 0 16px rgba(16,185,129,0.35)" : "none" }}
            >
              {fmtUsdK(rev)}
            </span>
            {phase >= 1 && (
              <span
                className="text-[10px] sm:text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-[1px]"
                style={{ animation: "cap-in 400ms ease-out both" }}
              >
                +{fmtUsdK(rev - 48700)}
              </span>
            )}
          </div>
          <div className="text-[10px] sm:text-xs text-slate-500 mt-0.5">Revenue Velocity · 30d</div>

          {/* Margin gauge */}
          <div className="mt-4 flex items-center gap-3">
            <RingGauge value={margin} size={64} color={glow ? "#059669" : "#10b981"} pulse={glow} />
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-slate-900 text-sm sm:text-base font-extrabold tabular-nums">{margin.toFixed(1)}%</span>
                {phase >= 2 && (
                  <span
                    className="text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-[1px]"
                    style={{ animation: "cap-in 400ms ease-out both" }}
                  >
                    +{(margin - 48).toFixed(1)}pt
                  </span>
                )}
              </div>
              <div className="text-[9px] tracking-[0.14em] uppercase font-bold text-slate-500">Net margin</div>
            </div>
          </div>

          {/* ROI card */}
          <div
            className="mt-auto pt-3 transition-all"
            style={{
              opacity: roiShown ? 1 : 0,
              transform: roiShown ? "translateY(0)" : "translateY(8px)",
            }}
          >
            <div className="text-[10px] tracking-[0.14em] uppercase font-bold text-emerald-700">ROI · last 30d</div>
            <div
              className="text-emerald-700 text-2xl font-extrabold tabular-nums"
              style={{
                textShadow: glow ? "0 0 20px rgba(16,185,129,0.4)" : "none",
                animation: roiShown ? "pd-pop 500ms cubic-bezier(.2,.8,.2,1) both" : "none",
              }}
            >
              {ROI_MULTIPLIER.toFixed(2)}x
            </div>
          </div>
        </div>
      </div>

      <SceneCaption
        kicker={<><TrendingUp className="w-3 h-3" /> Growth</>}
        headline="Grow revenue"
        accent="AND expand margin — in one click."
      />
    </div>
  );
};

// Small ring gauge used inside S4.
const RingGauge = ({ value, size = 64, color = "#10b981", pulse = false }) => {
  const circ = 2 * Math.PI * 28;
  const dash = circ * (value / 100);
  return (
    <div
      className="relative shrink-0"
      style={{
        width: size, height: size,
        animation: pulse ? "pd-slow-pulse 2.2s ease-in-out infinite" : "none",
      }}
    >
      <svg viewBox="0 0 64 64" className="w-full h-full -rotate-90">
        <circle cx="32" cy="32" r="28" fill="none" stroke="#e2e8f0" strokeWidth="6" />
        <circle
          cx="32" cy="32" r="28" fill="none" stroke={color} strokeWidth="6" strokeLinecap="round"
          strokeDasharray={`${dash} ${circ - dash}`}
          style={{ transition: "stroke-dasharray 500ms ease-out" }}
        />
      </svg>
    </div>
  );
};

// ==================== S5 · End card ====================
const SceneEndCard = () => (
  <div
    className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center"
    style={{
      background:
        "radial-gradient(60% 60% at 50% 40%, rgba(16,185,129,0.20) 0%, transparent 70%), linear-gradient(180deg,#0f172a 0%,#1e293b 100%)",
    }}
  >
    <img src="/logo.png" alt="AI Growth Operator"
      className="w-16 h-16 rounded-2xl object-contain shadow-2xl mb-4"
      style={{ animation: "pd-glow 2.4s ease-in-out infinite" }}
    />
    <div
      className="text-white text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight max-w-2xl leading-tight"
      style={{ animation: "pd-fadein 500ms ease-out both" }}
    >
      Grow revenue. <span className="text-emerald-400">Expand margin.</span>
    </div>
    <div
      className="text-white/80 text-sm sm:text-base mt-2 max-w-md"
      style={{ animation: "pd-fadein-slow 700ms ease-out 300ms both" }}
    >
      Start free · connect Shopify · unlock Pro in-app.
    </div>
    <a
      href={PLATFORM_URL}
      target="_blank" rel="noopener noreferrer"
      data-testid="demo-cta-end-card"
      className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold shadow-2xl shadow-emerald-500/40 transition"
      style={{ animation: "pd-pop 500ms cubic-bezier(.2,.8,.2,1) 600ms both" }}
    >
      Start free · connect Shopify <ArrowUpRight className="w-4 h-4" />
    </a>
  </div>
);

export const SCENES = [
  SceneProblem,
  SceneDiagnose,
  SceneImplement,
  SceneGrowthPayoff,
  SceneEndCard,
];

export const N_SCENES = SCENES.length;
